/**
 * Station UI tuning and display text (`src/ui/ui.json`, Zod-validated once at import): stepper sizes, the default
 * buy quantity, the price-hint threshold, the game title, and the player-facing names of factions, slots, tabs and contract types.
 */
import { z } from 'zod'
import { CONTRACT_TYPES, SIM_FACTION_IDS, SLOT_IDS, type ContractType, type SimFactionId, type SlotId } from '../data/m2Schemas'
import { STATION_TABS, type StationTab } from './contracts'
import uiJson from './ui.json'

const label = z.string().min(1)

function labels<K extends string>(keys: readonly K[]): z.ZodType<Record<K, string>> {
  return z.record(z.string(), label).superRefine((rec, ctx) => {
    for (const k of keys) if (rec[k] === undefined) ctx.addIssue({ code: 'custom', path: [k], message: `missing label for "${k}"` })
    for (const k of Object.keys(rec)) {
      if (!(keys as readonly string[]).includes(k)) ctx.addIssue({ code: 'custom', path: [k], message: `unknown key "${k}"` })
    }
  }) as unknown as z.ZodType<Record<K, string>>
}

export const uiConfigSchema = z.strictObject({
  /** The game's player-facing name (H7, D-036): the pause brand, the start and loading overlays, `index.html <title>`. */
  gameTitle: label,
  steps: z.strictObject({
    /** Market quantity stepper, tonnes. */
    tonnes: z.number().positive(),
    /** Partial repair stepper, hull points. */
    repairPoints: z.number().int().positive(),
    /** Partial rearm stepper, torpedoes. */
    torpedoes: z.number().int().positive(),
    /** Lease Office "pay extra" stepper, credits. */
    payExtra: z.number().int().positive(),
  }),
  /** Tonnes a buy row starts at (clamped to what fits and what the wallet covers). */
  buyDefaultT: z.number().positive(),
  /** Lease history rows shown (newest first). */
  historyRows: z.number().int().positive(),
  /** A "better price at …" hint needs at least this fractional gain over the local price. */
  hintMinGain: z.number().min(0),
  /** PT-2 (D-803): Market sell-row copy; `{qty}` is filled with tonnes. */
  market: z.object({
    /** The buyer's room line ("Room 40 t"). */
    roomLine: z.string().min(1),
    /** The berth offer when the hold holds more than fits ("Store the other 35 t in your berth (free)"). */
    storeRest: z.string().min(1),
    /**
     * CL-3: a buy row's best sale in this system ("Best sale: Charter Hub · 293 cr/t · ▼ −9% (after tariffs)");
     * `{station}`, `{unit}` and `{margin}` (the glyph and the signed percent). Quotes include the claim tariff.
     */
    bestSale: label,
    /** CL-3: a buy row whose ware or good no other station of this system buys. */
    noBuyer: label,
    /** CL-3: the margin glyphs (never colour alone): a gain, a loss, and a margin that rounds to 0%. */
    marginUp: label,
    marginDown: label,
    marginFlat: label,
    /** CL-3 (Q8): the hold summary shown when nothing held sells here; `{items}` joins the item lines with "; ". */
    holdSummary: label,
    /** `{qty}`, `{name}`, `{station}`, `{unit}`. */
    holdItemSells: label,
    /** `{qty}`, `{name}`. */
    holdItemNoBuyer: label,
    /** `{n}`: the held items past `holdSummaryItems`. */
    holdMore: label,
    /** The most held items the summary names before "+N more". */
    holdSummaryItems: z.number().int().positive(),
    /** CL-4: the one line shown with nothing aboard (instead of disabled Sell rows). */
    holdEmpty: label,
    /** CL-4: the footer pointing at prices further out (the map's Routes layer). */
    routesFooter: label,
    /** CL-4: the section headings. */
    sections: z.object({ buy: label, sell: label, parts: label }),
  }),
  factions: labels<SimFactionId>(SIM_FACTION_IDS),
  slots: labels<SlotId>(SLOT_IDS),
  tabs: labels<StationTab>(STATION_TABS),
  contractTypes: labels<ContractType>(CONTRACT_TYPES),
})

export type UiConfig = z.infer<typeof uiConfigSchema>

export function parseUiConfig(raw: unknown): UiConfig {
  return uiConfigSchema.parse(raw)
}

/** The bundled UI config. */
export const UI_CONFIG: UiConfig = parseUiConfig(uiJson)

/** "Umbra Reach": the one game-title constant (H7, D-TBD-17); `index.html` carries the same text (a unit test checks). */
export const GAME_TITLE: string = UI_CONFIG.gameTitle
